import { useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import {
  buildPublicShowcaseNavEntries,
  type PublicShowcaseNavEntry,
} from '~/components/v2/components/showcase/publicShowcaseNavEntries';
import { type ShowcaseSubPage } from '~/components/v2/components/showcase/showcaseSubPageRoutes';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

// Module scope so an un-authored link doesn't hand a fresh array to the builder on every render.
const EMPTY_SUB_PAGES: ShowcaseSubPage[] = [];

export type PublicShowcaseNavEntries = {
  entries: PublicShowcaseNavEntry[];
  isVisible: boolean;
};

/**
 * The share link's nav destinations plus whether the navigation chrome should render at all. Both
 * the sticky bar and the mobile menu read this hook, so they always show the same destinations and
 * appear/disappear together. Active state comes from the layout contract's `activePage`, so
 * every surface — live page, Preview iframe, Edit tab — highlights the same entry the rest of the
 * tree renders.
 */
export const usePublicShowcaseNavEntries = (): PublicShowcaseNavEntries => {
  const { presentationPages } = useShortIdContext();
  const { activePage } = usePublicShowcaseContext();
  const subPages = presentationPages?.subPages ?? EMPTY_SUB_PAGES;
  const activeSlug = activePage?.pageSlug ?? null;

  const entries = useMemo(() => buildPublicShowcaseNavEntries({ activeSlug, subPages }), [activeSlug, subPages]);

  return {
    entries,
    // A single entry means Home alone — a link with no sub-pages, or one whose only entries were
    // dropped as unreachable. There is nothing to navigate between, so the chrome stays off and the
    // page renders exactly as it did before sub-pages existed. `hideNavigation` is the authored
    // opt-out; absent (links saved before the field existed) renders navigation.
    isVisible: !presentationPages?.hideNavigation && entries.length > 1,
  };
};
